// Matching the new-question page's cards to what the user is typing. Pure.
//
// The typed words are scored against each question's title and tags on every keystroke, rare
// words counting for more than common ones. Once typing pauses, Sayer's fast model names the
// specific subjects of the text ("Federal Reserve", "Taylor Swift") and broader related topics
// (hooks/useFeaturedSearch.js): a question about a named subject comes first, and the related
// topics reach questions that share a subject but not a word.

const STOP = new Set(
  (
    "a an and are as at be been before being by can could did do does during for from had has " +
    "have how if in into is it its will would of on or over than that the their them then there " +
    "these they this those through to under until up upon was we were what when where which who " +
    "whom why with within without year years next end any more most less least much many new " +
    "get gets happen happens occur say says said make makes per vs versus about after again ask " +
    "think forecast forecasts chance probability likely likelihood odds question"
  ).split(" "),
);
// Short words still worth matching; anything else under three letters is noise.
const SHORT_OK = new Set([
  "ai",
  "uk",
  "us",
  "eu",
  "un",
  "ev",
  "f1",
  "oil",
  "gdp",
  "cpi",
  "fed",
  "ecb",
  "boj",
  "nba",
  "nfl",
  "mlb",
  "nhl",
  "ipo",
  "war",
]);

// Weights. A named subject outranks any number of loose word matches; related topics only order
// the rest, so they are capped.
const TYPED_TITLE = 3;
const TYPED_PREFIX = 2;
const TYPED_TAG = 1.2;
const TWO_TYPED_BONUS = 1.5;
const SUBJECT = 6;
const SUBJECT_MORE = 1;
const RELATED_TITLE = 0.8;
const RELATED_TAG = 0.4;
const RELATED_CAP = 2.4;
const DOMAIN = 0.5;
const MIN_SCORE = 2.5;
const SHARE_OF_BEST = 0.5;

/**
 * A word reduced to a rough stem, so "rates" meets "rate", "cutting" meets "cut" and "raised"
 * meets "raise". Not real words; only ever compared with each other.
 */
export function stem(w) {
  let s = w;
  if (s.length > 5 && s.endsWith("ing")) s = s.slice(0, -3);
  else if (s.length > 4 && s.endsWith("ies")) s = `${s.slice(0, -3)}y`;
  else if (s.length > 4 && /(ch|sh|x|z|ss)es$/.test(s)) s = s.slice(0, -2);
  else if (s.length > 3 && s.endsWith("s") && !s.endsWith("ss")) s = s.slice(0, -1);
  else if (s.length > 4 && s.endsWith("ed")) s = s.slice(0, -2);
  // A final e drops everywhere, so the stripped forms above meet their base word.
  if (s.length > 3 && s.endsWith("e")) s = s.slice(0, -1);
  if (s.length > 3 && s.at(-1) === s.at(-2)) s = s.slice(0, -1);
  return s;
}

function words(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/['’]s\b/g, "")
    .split(/[^a-z0-9]+/)
    .filter((w) => w && !STOP.has(w) && (w.length >= 3 || SHORT_OK.has(w)));
}

/**
 * The typed text as query terms. The last word is `partial` while the user is still typing it,
 * so it matches by prefix ("tai" → Taiwan).
 * @returns {{t: string, partial: boolean}[]}
 */
export function queryTerms(text) {
  const raw = String(text || "");
  const list = words(raw);
  const typingLast = raw.length > 0 && /[a-z0-9]$/i.test(raw);
  const seen = new Set();
  const out = [];
  list.forEach((w, i) => {
    const partial = typingLast && i === list.length - 1;
    const t = partial ? w : stem(w);
    if (seen.has(t)) return;
    seen.add(t);
    out.push({ t, partial });
  });
  return out;
}

/** Named subjects as stem lists: every word of one must appear for it to count. */
export function subjectPhrases(list) {
  const out = [];
  const seen = new Set();
  for (const phrase of Array.isArray(list) ? list : []) {
    const ws = words(phrase).map(stem);
    const key = ws.join(" ");
    if (ws.length && !seen.has(key)) {
      seen.add(key);
      out.push(ws);
    }
  }
  return out;
}

/** Related topics (single words or short phrases) as stems, minus the typed ones. */
export function relatedTerms(list, typed = []) {
  const have = new Set(typed.map((q) => q.t));
  const out = new Set();
  for (const phrase of Array.isArray(list) ? list : []) {
    for (const w of words(phrase)) {
      const s = stem(w);
      if (!have.has(s)) out.add(s);
    }
  }
  return [...out];
}

/** What one question is matched on: its title words and its tags. */
export function indexItem(item) {
  const tags = item?.tags || {};
  return {
    item,
    words: new Set(words(`${item?.title || ""} ${item?.marketTitle || ""}`).map(stem)),
    tags: new Set(
      words([tags.domain, tags.category, tags.force, tags.region].filter(Boolean).join(" ")).map(
        stem,
      ),
    ),
    domain: String(tags.domain || "").toLowerCase(),
  };
}

/** Every question indexed, with how many titles use each word (rare words weigh more). */
export function buildIndex(items) {
  const entries = (Array.isArray(items) ? items : []).map(indexItem);
  const df = new Map();
  for (const ix of entries) for (const w of ix.words) df.set(w, (df.get(w) || 0) + 1);
  return { entries, df, n: entries.length };
}

function hasPrefix(set, p) {
  for (const w of set) if (w.startsWith(p)) return true;
  return false;
}

/** How rare a typed term is among the titles: 0.6 (in all of them) to 1.6 (in none). */
function rarity(index, { t, partial }) {
  let df = index.df.get(t) || 0;
  if (partial) for (const [w, c] of index.df) if (w !== t && w.startsWith(t)) df += c;
  return 0.6 + Math.log(1 + index.n / (1 + df)) / Math.log(1 + Math.max(1, index.n));
}

/**
 * How well one question matches; 0 when nothing typed, named or related touches it. A domain
 * alone never makes a match, it only orders matches.
 *
 * @param {ReturnType<typeof indexItem>} ix
 * @param {{terms: {t: string, partial: boolean, w?: number}[], subjects?: string[][],
 *   related?: string[], domains?: Set<string>}} q  `w` is the term's rarity (1 when absent)
 */
export function scoreItem(ix, { terms, subjects = [], related = [], domains = new Set() }) {
  let score = 0;
  let hits = 0;
  let typedHits = 0;
  for (const { t, partial, w = 1 } of terms) {
    if (ix.words.has(t)) score += TYPED_TITLE * w;
    else if (partial && t.length >= 3 && hasPrefix(ix.words, t)) score += TYPED_PREFIX * w;
    else if (ix.tags.has(t) || (partial && t.length >= 3 && hasPrefix(ix.tags, t)))
      score += TYPED_TAG * w;
    else continue;
    hits += 1;
    typedHits += 1;
  }
  if (typedHits >= 2) score += TWO_TYPED_BONUS;
  // Subjects often name one thing several ways (Fed, FOMC): the rarest match carries the weight.
  let named = 0;
  let bestSubject = 0;
  for (const { ws, w = 1 } of subjects) {
    if (!ws.every((s) => ix.words.has(s) || ix.tags.has(s))) continue;
    named += 1;
    bestSubject = Math.max(bestSubject, SUBJECT * w);
  }
  if (named) {
    score += bestSubject + (named - 1) * SUBJECT_MORE;
    hits += named;
  }
  let rel = 0;
  for (const r of related) {
    if (ix.words.has(r)) rel += RELATED_TITLE;
    else if (ix.tags.has(r)) rel += RELATED_TAG;
    else continue;
    hits += 1;
  }
  if (!hits) return { score: 0, named: 0 };
  return {
    score: score + Math.min(RELATED_CAP, rel) + (domains.has(ix.domain) ? DOMAIN : 0),
    named,
  };
}

/**
 * The best-matching questions, best first: a question about a named subject before any other,
 * then score, then one of the page's own cards, then the newest forecast. A match far behind the
 * best one is left out, so a strong match is not padded with weak ones.
 *
 * @param {ReturnType<typeof buildIndex>} index
 * @param {{terms: {t: string, partial: boolean}[], subjects?: string[][], related?: string[],
 *   domains?: Set<string>}} q
 * @param {{limit?: number, exclude?: Set<string>}} [opts]
 */
export function searchFeatured(index, q, { limit = 4, exclude = new Set() } = {}) {
  if (!q.terms.length && !(q.subjects || []).length) return [];
  const query = {
    ...q,
    terms: q.terms.map((t) => ({ ...t, w: rarity(index, t) })),
    // A subject as common as a topic ("interest rates") weighs like one.
    subjects: (q.subjects || []).map((ws) => ({
      ws,
      w: Math.max(...ws.map((t) => rarity(index, { t, partial: false }))) / 1.6,
    })),
  };
  const scored = [];
  for (const ix of index.entries) {
    if (exclude.has(ix.item.questionKey)) continue;
    const { score, named } = scoreItem(ix, query);
    if (score >= MIN_SCORE) scored.push({ ix, s: score, named: named > 0 });
  }
  scored.sort(
    (a, b) =>
      Number(b.named) - Number(a.named) ||
      b.s - a.s ||
      Number(b.ix.item.featured !== false) - Number(a.ix.item.featured !== false) ||
      String(b.ix.item.firedAt || "").localeCompare(String(a.ix.item.firedAt || "")),
  );
  const best = scored.reduce((m, x) => Math.max(m, x.s), 0);
  return scored
    .filter(({ s }) => s >= best * SHARE_OF_BEST)
    .slice(0, limit)
    .map(({ ix }) => ix.item);
}

/** The prompt that asks Sayer what the text is about; its reply is JSON. */
export function relatedPrompt(text, domainNames) {
  return [
    "Someone is typing a question they want forecast. Say what it is about, so related forecast",
    "questions can be found even when they use different words.",
    `Text: ${JSON.stringify(String(text).slice(0, 300))}`,
    "Reply with JSON only, no prose:",
    '{"subjects": [up to 6 specific named things it is about: people, organisations, places,',
    "events, products, written as a headline would, plus common short forms (Fed, FOMC). Proper",
    "names only: never general topics such as interest rates, inflation or elections],",
    '"related": [up to 10 broader topics, synonyms or neighbouring subjects],',
    `"domains": [0 to 2 of: ${domainNames.join(", ")}]}`,
  ].join("\n");
}

/** The model's reply as {subjects, related, domains}, or null when it isn't the JSON asked for. */
export function parseRelated(reply) {
  const m = /\{[\s\S]*\}/.exec(String(reply || ""));
  if (!m) return null;
  try {
    const o = JSON.parse(m[0]);
    const strings = (v, n) =>
      Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, n) : [];
    return {
      subjects: strings(o.subjects, 8),
      related: strings(o.related ?? o.terms, 16),
      domains: strings(o.domains, 3),
    };
  } catch {
    return null;
  }
}
